import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { trackPage, trackClick } from "@/lib/track";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/context/FiltersContext";
import usePersistentState from "@/lib/usePersistentState";
import { DateControls } from "@/components/DateControls";
import { ASSOCIATIONS, EVENT_CATEGORIES, daysUntilMountain } from "@/lib/ui";
import useRegions from "@/lib/useRegions";
import Onboarding from "@/components/Onboarding";
import PaywallModal from "@/components/PaywallModal";
import VoiceAssistant from "@/components/VoiceAssistant";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MobileFilters from "@/components/mobile/MobileFilters";
import { EntryClosingTicker } from "@/components/EntryClosingTicker";
import { CowIcon } from "@/components/icons/CowIcon";
import RodeoInfoDialog from "@/components/RodeoInfoDialog";
import NotificationsBell from "@/components/NotificationsBell";
import { CreditsChip } from "@/components/CreditsChip";
import { useCredits } from "@/context/CreditsContext";
import { isFan, FAN_NAV_HIDE } from "@/lib/fan";
import {
  Calendar, Map, BookmarkCheck, Bell, Users, Award, Settings, Globe,
  RefreshCw, LogOut, Crown, Check, LayoutDashboard, SlidersHorizontal, Navigation, TrendingUp, MessageSquareWarning, MessageSquare, Shield, Target, Trophy, ArrowLeftRight, Banknote, ListTodo, ListChecks, Medal, Menu, Columns2, Table, ChevronUp, ChevronDown, HelpCircle, Route as RouteIcon, CalendarClock, Crosshair, Radio, DollarSign, Coins, Eye,
} from "lucide-react";
import { Sparkles } from "lucide-react";

export const NAV = [
  { to: "/today", label: "Today", icon: Radio },
  { to: "/table", label: "Table", icon: Table },
  { to: "/calendar", label: "Calendar", icon: Calendar },
  { to: "/map", label: "Map", icon: Map },
  { to: "/riders", label: "My Riders", icon: Users },
  { to: "/schedule", label: "Schedule", icon: CalendarClock, fanOnly: true },
  { to: "/weekly", label: "Weekly Discovery", icon: Sparkles },
  { to: "/results", label: "Results", icon: Medal },
  { to: "/cfr", label: "CFR", icon: Trophy },
  { to: "/directory", label: "Directory", icon: Globe },
  { to: "/my-rodeos", label: "My Rodeos", icon: BookmarkCheck },
  { to: "/planner", label: "Can I Make It?", icon: Navigation },
  { to: "/route-mapper", label: "Route Mapper", icon: RouteIcon },
  { to: "/season", label: "My Season", icon: Award },
  { to: "/wallet", label: "Wallet", icon: Coins },
  { to: "/rodeo-counts", label: "My Rodeo Counts", icon: ListChecks },
  { to: "/standings", label: "Standings", icon: Trophy },
  { to: "/movers", label: "Movers", icon: TrendingUp },
  { to: "/payouts", label: "Top Money", icon: DollarSign },
  { to: "/nfr-cutline", label: "NFR Cutline", icon: Crosshair },
  { to: "/trades", label: "Trade List", icon: ArrowLeftRight },
  { to: "/goals", label: "Goals", icon: Target },
  { to: "/bank", label: "Bank", icon: Banknote },
  { to: "/stock", label: "Rodeo Stock", icon: CowIcon },
  { to: "/stats", label: "Pro Stats", icon: TrendingUp },
  { to: "/deadlines", label: "Deadlines", icon: Bell },
  { to: "/entry-dates", label: "Entry Dates", icon: CalendarClock },
  { to: "/guide", label: "Guide", icon: HelpCircle },
  { to: "/roadmap", label: "Coming Soon", icon: ListTodo },
];

const MOBILE_NAV = [
  { to: "/table", label: "Table", icon: Table },
  { to: "/calendar", label: "Calendar", icon: Calendar },
  { to: "/map", label: "Map", icon: Map },
  { to: "/riders", label: "My Riders", icon: Users },
];

// Fans get a spectator-first bottom bar.
const FAN_MOBILE_NAV = [
  { to: "/today", label: "Home", icon: Radio },
  { to: "/riders", label: "My Riders", icon: Users },
  { to: "/schedule", label: "Schedule", icon: CalendarClock },
  { to: "/weekly", label: "Discovery", icon: Sparkles },
];

// Routes where the Facebook-style auto-hide should be disabled (chrome always visible).
const NO_AUTOHIDE = new Set([]);

function Logo({ className = "h-10" }) {
  return (
    <img src="/rodeo-pilot-wordmark.webp" alt="Rodeo Pilot" data-testid="brand-logo" className={`w-auto object-contain ${className}`} />
  );
}

function ManageDialog({ open, onClose, title, all, selected, onToggle }) {
  const slug = title.toLowerCase().replace(/\s/g, "-");
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0C1017] text-slate-100 border border-white/10" data-testid={`manage-${slug}`}>
        <DialogHeader><DialogTitle className="text-white font-display">Edit {title}</DialogTitle></DialogHeader>
        <div className="space-y-0.5 mt-1">
          {all.map((x) => {
            const on = selected.has(x);
            return (
              <button key={x} onClick={() => onToggle(x)} data-testid={`manage-item-${x.replace(/[\s()]/g, "-")}`}
                className="w-full flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-white/5 text-left">
                <span className={`w-5 h-5 rounded flex items-center justify-center border ${on ? "bg-[#C8000F] border-[#C8000F]" : "border-slate-600"}`}>
                  {on && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                </span>
                <span className="text-sm text-slate-200">{x}</span>
              </button>
            );
          })}
        </div>
        <button onClick={onClose} data-testid={`manage-done-${slug}`} className="w-full mt-3 bg-[#C8000F] hover:bg-[#9E0008] text-white font-semibold py-2.5 rounded-lg">Done</button>
      </DialogContent>
    </Dialog>
  );
}

function FilterGroup({ title, all, selected, onToggle }) {
  const [open, setOpen] = useState(false);
  const chosen = all.filter((x) => selected.has(x));
  const slug = title.toLowerCase().replace(/\s/g, "-");
  return (
    <div className="px-3 py-3">
      <div className="flex items-center justify-between px-2 mb-2">
        <span className="text-[10px] font-bold tracking-wider text-slate-500 uppercase">{title}</span>
        <button onClick={() => setOpen(true)} data-testid={`edit-${slug}`} className="text-[10px] font-semibold text-[#C8000F] hover:underline">Edit</button>
      </div>
      <div className="flex flex-wrap gap-1.5 px-1">
        {chosen.length ? chosen.map((x) => (
          <button key={x} onClick={() => onToggle(x)} data-testid={`chip-${x.replace(/[\s()]/g, "-")}`}
            className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-[#C8000F]/15 text-[#F26D77] border border-[#C8000F]/30">
            {x}
          </button>
        )) : <span className="text-[11px] text-slate-500 px-1 py-1">None selected — tap Edit</span>}
      </div>
      <ManageDialog open={open} onClose={() => setOpen(false)} title={title} all={all} selected={selected} onToggle={onToggle} />
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const fan = isFan(user);
  const { credits } = useCredits();
  const creditsLow = typeof credits === "number" && credits <= 2;
  const { assoc, toggleAssoc, events, toggleEvent, minMoney, setMinMoney, status, setStatus, country, setCountry, province, setProvince, filtersOpen, setFiltersOpen } = useFilters();
  const loc = useLocation();
  const nav = useNavigate();
  const regions = useRegions();
  const provincesForCountry = (regions.find((r) => r.country === country)?.provinces) || [];

  const navItems = [...NAV];
  const isStaff = user?.role === "reviewer" || user?.role === "admin";
  const isAdmin = user?.role === "admin";
  const [reviewCount, setReviewCount] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);
  useEffect(() => {
    if (!user) return;
    const fetchUnread = () => api.get("/notifications/unread-count").then((r) => setNotifUnread(r.data.unread || 0)).catch(() => {});
    fetchUnread();
    const iv = setInterval(fetchUnread, 120000);
    window.addEventListener("rp-notifs-read", fetchUnread);
    return () => { clearInterval(iv); window.removeEventListener("rp-notifs-read", fetchUnread); };
  }, [user]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [closings, setClosings] = useState([]);
  const [tickerDismissed, setTickerDismissed] = useState(false); // session-only (reappears next app open)
  const [tickerInfo, setTickerInfo] = useState(null); // rodeo opened from the closing ticker
  useEffect(() => { api.get("/prca/entry-closings").then((r) => setClosings(r.data || [])).catch(() => {}); }, []);
  // Ticker shows ONLY rodeos whose entries close within the next 5 days (today → +5), then loops.
  const tickerItems = useMemo(
    () => (closings || []).filter((c) => { const dl = daysUntilMountain(c.close_iso); return dl != null && dl >= 0 && dl <= 5; }),
    [closings]
  );
  const showTicker = !fan && !tickerDismissed && tickerItems.length > 0;
  useEffect(() => {
    document.documentElement.classList.toggle("has-tick", showTicker);
    return () => document.documentElement.classList.remove("has-tick");
  }, [showTicker]);
  // Shared with MobileMap (same key) so the landscape header can host the map's expand toggle.
  const [mapHdrOpen, setMapHdrOpen] = usePersistentState("rp_m_map_hdr", false);
  const [navFlags, setNavFlags] = useState(() => {
    try { const c = JSON.parse(localStorage.getItem("rp_navflags")); return c && typeof c === "object" ? c : null; } catch { return null; }
  });
  useEffect(() => {
    if (isStaff) api.get("/reviewer/queue").then((r) => setReviewCount(r.data.open_count || 0)).catch(() => {});
  }, [isStaff]);
  useEffect(() => {
    api.get("/nav-flags").then((r) => {
      const f = r.data.flags || {};
      setNavFlags(f);
      try { localStorage.setItem("rp_navflags", JSON.stringify(f)); } catch { /* ignore */ }
    }).catch(() => setNavFlags({}));
  }, []);
  // Product analytics: page views on route change + key clicks (any element with a data-testid).
  useEffect(() => {
    const label = NAV.find((n) => n.to === loc.pathname)?.label || loc.pathname;
    trackPage(label, loc.pathname);
  }, [loc.pathname]);
  useEffect(() => {
    const onClick = (e) => {
      const el = e.target.closest("[data-testid]");
      if (el) trackClick(el.getAttribute("data-testid"));
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
  // Facebook-style auto-hide: hide header + bottom nav on scroll-down, show on scroll-up / at top.
  // Uses capture-phase scroll so it works for both window scroll and inner scroll panels (Table/Calendar/Map).
  const [chromeHidden, setChromeHidden] = useState(false);
  const autoHide = !NO_AUTOHIDE.has(loc.pathname);
  useEffect(() => {
    setChromeHidden(false);
    if (!autoHide) return;
    const pos = new WeakMap();
    const SHOW_AT_TOP = 6;   // always reveal chrome near the very top
    const THRESH = 56;       // must scroll this far in one direction before toggling
    const LOCK_MS = 420;     // ignore scroll right after a toggle so the layout
                             // reflow can't re-fire the handler (that was the jitter/repeat-jump)
    let acc = 0, lastDir = 0, locked = false, hidden = false;
    const isField = (el) => el && /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName || "");
    const getTop = (el) => (el === document || el === document.documentElement || el === document.body)
      ? (window.scrollY || document.documentElement.scrollTop || 0)
      : (typeof el.scrollTop === "number" ? el.scrollTop : null);
    const setHidden = (v) => {
      if (v === hidden) return;
      hidden = v; setChromeHidden(v);
      locked = true; setTimeout(() => { locked = false; }, LOCK_MS);
    };
    const onScroll = (e) => {
      // While a form field is focused iOS auto-scrolls it into view — never toggle then.
      if (isField(document.activeElement)) return;
      const el = e.target;
      const st = getTop(el);
      if (st == null) return;
      const prev = pos.has(el) ? pos.get(el) : st;
      pos.set(el, st);           // keep position fresh even while locked
      if (locked) return;
      if (st <= SHOW_AT_TOP) { acc = 0; setHidden(false); return; }
      const d = st - prev;
      if (d === 0) return;
      const dir = d > 0 ? 1 : -1;
      if (dir !== lastDir) { acc = 0; lastDir = dir; } // reset on direction change
      acc += d;
      if (dir > 0 && acc > THRESH) { acc = 0; setHidden(true); }        // scrolling down → hide
      else if (dir < 0 && acc < -THRESH) { acc = 0; setHidden(false); } // scrolling up → show
    };
    const onFocusIn = (e) => { if (isField(e.target)) setHidden(false); };
    document.addEventListener("scroll", onScroll, true);
    document.addEventListener("focusin", onFocusIn, true);
    return () => { document.removeEventListener("scroll", onScroll, true); document.removeEventListener("focusin", onFocusIn, true); };
  }, [autoHide, loc.pathname]);
  useEffect(() => {
    const el = document.documentElement;
    if (chromeHidden) el.classList.add("chrome-hidden"); else el.classList.remove("chrome-hidden");
    return () => el.classList.remove("chrome-hidden");
  }, [chromeHidden]);
  // Until flags are known (fetched or cached), non-admins see nothing rather than a flash of the full menu.
  const flagsReady = isAdmin || navFlags !== null;
  const released = (to) => flagsReady && (navFlags || {})[to] !== false;
  // Admin sees everything (with a "hidden" tag); users see only released items.
  const visibleNav = isAdmin ? navItems : (flagsReady ? navItems.filter((n) => released(n.to) && !(fan && FAN_NAV_HIDE.has(n.to)) && !(n.fanOnly && !fan)) : []);
  const dlItem = visibleNav.find((n) => n.to === "/deadlines");
  if (dlItem) dlItem.badge = notifUnread || undefined;
  if (isStaff) {
    visibleNav.push({ to: "/review", label: "Review Queue", icon: MessageSquareWarning, badge: reviewCount || undefined, badgeAmber: true });
  }
  if (isAdmin) {
    visibleNav.push({ to: "/admin", label: "Admin", icon: LayoutDashboard });
    visibleNav.push({ to: "/settings", label: "Settings", icon: Settings });
  }
  const mobileNav = fan ? FAN_MOBILE_NAV : MOBILE_NAV;

  return (
    <div className="min-h-screen bg-[#0C1017] lg:bg-white">
      <Onboarding />
      <PaywallModal />
      <VoiceAssistant />
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-[#0C1017] border-r border-black/40 z-40">
        <div className="px-4 py-5 border-b border-white/10 flex items-center justify-between gap-2">
          <Logo className="h-14" />
          <NotificationsBell />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
        <nav className="px-3 py-3 space-y-0.5">
          {visibleNav.map((n) => {
            const active = loc.pathname === n.to;
            const Icon = n.icon;
            const hidden = isAdmin && n.to !== "/admin" && n.to !== "/settings" && n.to !== "/review" && !released(n.to);
            return (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? "bg-[#C8000F] text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"} ${hidden ? "opacity-55" : ""}`}>
                <Icon className="w-[18px] h-[18px]" />
                <span className="flex-1">{n.label}</span>
                {hidden && <span data-testid={`nav-hidden-${n.to}`} className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/10 text-slate-400">Hidden</span>}
                {n.badge && <span className={`w-5 h-5 rounded-full text-white text-[11px] font-bold flex items-center justify-center ${n.badgeAmber ? "bg-[#E68A2E]" : "bg-[#C8000F]"}`}>{n.badge}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-white/10">
          <div className="px-3 py-3 border-b border-white/10">
            <div className="px-2 mb-2 text-[10px] font-bold tracking-wider text-slate-500 uppercase">Country</div>
            <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="filter-country"
              className="w-full bg-white/5 text-slate-200 text-[13px] rounded-md px-2.5 py-2 border border-white/10 focus:outline-none focus:border-[#C8000F]">
              <option value="" className="bg-[#0C1017]">All countries</option>
              {regions.map((r) => <option key={r.country} value={r.country} className="bg-[#0C1017]">{r.country} ({r.count})</option>)}
            </select>
            {country && (
              <>
                <div className="px-2 mt-3 mb-2 text-[10px] font-bold tracking-wider text-slate-500 uppercase">Province / State</div>
                <select value={province} onChange={(e) => setProvince(e.target.value)} data-testid="filter-province"
                  className="w-full bg-white/5 text-slate-200 text-[13px] rounded-md px-2.5 py-2 border border-white/10 focus:outline-none focus:border-[#C8000F]">
                  <option value="" className="bg-[#0C1017]">All provinces</option>
                  {provincesForCountry.map((p) => <option key={p.province} value={p.province} className="bg-[#0C1017]">{p.province} ({p.count})</option>)}
                </select>
              </>
            )}
          </div>
          <div className="px-3 py-3 border-b border-white/10">
            <div className="px-2 mb-2 text-[10px] font-bold tracking-wider text-slate-500 uppercase">Entries</div>
            <div className="flex flex-wrap gap-1.5 px-1">
              {[{ v: "", l: "All" }, { v: "upcoming", l: "Entries Open" }, { v: "closed", l: "Entries Closed" }].map((s) => (
                <button key={s.v} onClick={() => setStatus(s.v)} data-testid={`filter-status-${s.v || "all"}`}
                  className={`text-[12px] px-2.5 py-1 rounded-md ${status === s.v ? "bg-[#C8000F] text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>{s.l}</button>
              ))}
            </div>
            <div className="px-2 mt-3 mb-2 text-[10px] font-bold tracking-wider text-slate-500 uppercase">Added Money</div>
            <div className="flex flex-wrap gap-1.5 px-1">
              {[0, 7500, 15000, 30000].map((m) => (
                <button key={m} onClick={() => setMinMoney(m)} data-testid={`filter-money-${m}`}
                  className={`text-[12px] px-2.5 py-1 rounded-md ${minMoney === m ? "bg-[#C8000F] text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>{m === 0 ? "Any" : `$${m / 1000}k+`}</button>
              ))}
            </div>
          </div>
          <FilterGroup title="My Associations" all={ASSOCIATIONS} selected={assoc} onToggle={toggleAssoc} />
          <div className="border-t border-white/10" />
          <FilterGroup title={fan ? "Events I Watch" : "Events I Enter"} all={EVENT_CATEGORIES} selected={events} onToggle={toggleEvent} />
        </div>
        </div>

        <div className="border-t border-white/10 p-3">
          {!fan && (
            <button onClick={() => nav("/wallet")} data-testid="sidebar-credits-btn"
              className={`w-full flex items-center justify-between gap-2 mb-2 px-3 py-2 rounded-lg border transition-colors ${creditsLow ? "border-[#C8000F]/50 bg-[#C8000F]/10 hover:bg-[#C8000F]/15" : "border-[#E6B35A]/30 bg-[#E6B35A]/10 hover:bg-[#E6B35A]/15"}`}>
              <span className={`flex items-center gap-2 text-sm font-semibold ${creditsLow ? "text-[#FF7A82]" : "text-[#E6B35A]"}`}><Coins className="w-4 h-4" /> Rodeo Credits</span>
              <span className={`font-display font-extrabold text-sm ${creditsLow ? "text-[#FF7A82]" : "text-[#E6B35A]"}`} data-testid="sidebar-credits-balance">{credits == null ? "—" : credits.toLocaleString()}</span>
            </button>
          )}
          {!fan && (user?.is_pro ? (
            <div className="flex items-center gap-2 px-2 py-2 text-[#E68A2E] text-xs font-semibold"><Crown className="w-4 h-4 text-[#C8000F]" /> PRO Member</div>
          ) : (
            <button onClick={() => nav("/choose-plan")} data-testid="upgrade-btn" className="w-full flex items-center justify-center gap-2 mb-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-semibold py-2 rounded-lg">
              <Crown className="w-4 h-4" /> Go Pro
            </button>
          ))}
          {fan && (
            <button onClick={() => nav("/choose-plan")} data-testid="fan-upgrade-btn"
              className="w-full flex items-center justify-center gap-2 mb-2 text-sm font-bold py-2 rounded-lg text-[#1A1205]"
              style={{ background: "#E6B35A" }}>
              <Eye className="w-4 h-4" /> Follow more riders
            </button>
          )}
          <Link to="/contact" data-testid="sidebar-feedback-btn"
            className="w-full flex items-center justify-center gap-2 mb-2 border border-white/15 text-slate-300 hover:text-white hover:bg-white/5 text-sm font-semibold py-2 rounded-lg transition-colors">
            <MessageSquare className="w-4 h-4" /> Send feedback
          </Link>
          <div className="flex items-center justify-between px-1">
            <Link to="/season" data-testid="sidebar-profile-link" className="flex items-center gap-2 text-slate-400 text-[11px] hover:text-white group">
              <span className="w-7 h-7 rounded-full bg-[#C8000F]/20 text-[#F26D77] flex items-center justify-center font-bold text-xs">{(user?.name || "?").charAt(0).toUpperCase()}</span>
              <div>
                <div className="text-slate-300 font-medium group-hover:text-white">{user?.name}</div>
                <div className="group-hover:underline">View profile</div>
              </div>
            </Link>
            <button onClick={logout} data-testid="logout-btn" title="Log out" className="text-slate-500 hover:text-white p-1.5"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar (dark) — auto-hides on scroll down. Wraps the logo row + ticker so they slide together. */}
      <div style={{ transform: chromeHidden ? "translateY(-100%)" : "translateY(0)" }}
        className="lg:hidden fixed top-0 inset-x-0 z-40 transition-transform duration-200 will-change-transform">
      <header
        className="bg-[#0C1017] border-b border-white/10 px-4 h-14 max-lg:landscape:h-11 flex items-center justify-between">
        <Logo className="h-10 max-lg:landscape:h-8" />
        {/* Landscape only: pull the date controls up onto the logo row to reclaim vertical space. */}
        <div className="hidden max-lg:landscape:flex flex-1 min-w-0 items-center gap-1.5 pl-3 overflow-x-auto no-scrollbar" data-testid="mobile-header-daterow">
          {(loc.pathname === "/" || loc.pathname === "/table" || loc.pathname === "/map" || loc.pathname === "/calendar") && <DateControls dark />}
          {loc.pathname === "/map" && (
            <button onClick={() => setMapHdrOpen((v) => !v)} data-testid="m-map-header-toggle-landscape"
              className="shrink-0 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-300 active:bg-white/10 flex items-center">
              {mapHdrOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => nav("/today")} data-testid="mobile-today-btn" className="text-[#C8000F] p-2" title="Today"><Radio className="w-5 h-5" /></button>
          {released("/payouts") && <button onClick={() => nav("/payouts")} data-testid="mobile-payouts-btn" className="text-[#16A34A] p-2" title="Top Money by Event"><DollarSign className="w-5 h-5" /></button>}
          {released("/directory") && <button onClick={() => nav("/directory")} data-testid="mobile-directory-btn" className="text-slate-300 p-2"><Globe className="w-5 h-5" /></button>}
          {released("/standings") && <button onClick={() => nav("/standings")} data-testid="mobile-standings-btn" className="text-slate-300 p-2"><Trophy className="w-5 h-5" /></button>}
          {released("/stock") && <button onClick={() => nav("/stock")} data-testid="mobile-stock-btn" className="text-slate-300 p-2"><CowIcon className="w-5 h-5" /></button>}
          {released("/stats") && <button onClick={() => nav("/stats")} data-testid="mobile-stats-btn" className="text-slate-300 p-2"><TrendingUp className="w-5 h-5" /></button>}
          {!fan && <CreditsChip dark className="ml-0.5" />}
          <NotificationsBell m />
          <button onClick={() => setFiltersOpen(true)} data-testid="mobile-filters-btn" className="text-slate-300 p-2"><SlidersHorizontal className="w-5 h-5" /></button>
          <button onClick={logout} data-testid="mobile-logout-btn" className="text-slate-300 p-2"><LogOut className="w-5 h-5" /></button>
        </div>
      </header>
        {showTicker && <EntryClosingTicker items={tickerItems} onClick={() => nav("/entry-dates")} onItem={(c) => setTickerInfo({ id: c.rodeo_id, name: c.rodeo })} onDismiss={() => setTickerDismissed(true)} />}
      </div>

      <MobileFilters open={filtersOpen} onClose={() => setFiltersOpen(false)} />

      <main style={{ paddingTop: "var(--hdr)", paddingBottom: "var(--ftr)" }} className="lg:ml-64 transition-[padding] duration-200">
        <div className={`hidden lg:block sticky top-0 z-30 ${showTicker ? "h-7" : ""}`}>{showTicker && <EntryClosingTicker items={tickerItems} onClick={() => nav("/entry-dates")} onItem={(c) => setTickerInfo({ id: c.rodeo_id, name: c.rodeo })} onDismiss={() => setTickerDismissed(true)} />}</div>
        {children}
      </main>

      {/* Mobile bottom tab bar — auto-hides on scroll down */}
      <nav style={{ transform: chromeHidden ? "translateY(100%)" : "translateY(0)" }}
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0C1017] border-t border-white/10 grid grid-cols-5 transition-transform duration-200 will-change-transform">
        {mobileNav.map((n) => {
          const active = loc.pathname === n.to;
          const Icon = n.icon;
          return (
            <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`flex flex-col items-center justify-center py-2.5 max-lg:landscape:py-1.5 gap-0.5 text-[9.5px] leading-none text-center px-0.5 ${active ? "text-[#C8000F]" : "text-slate-400"}`}>
              <Icon className="w-[18px] h-[18px]" /> {n.label}
            </Link>
          );
        })}
        <button onClick={() => setMoreOpen(true)} data-testid="mobile-nav-more"
          className="flex flex-col items-center justify-center py-2.5 max-lg:landscape:py-1.5 gap-0.5 text-[9.5px] leading-none text-center px-0.5 text-slate-400">
          <Menu className="w-[18px] h-[18px]" /> More
        </button>
      </nav>

      {/* Mobile "More" menu — everything not on the bottom tabs */}
      <Dialog open={moreOpen} onOpenChange={setMoreOpen}>
        <DialogContent className="bg-[#0C1017] text-slate-100 border border-white/10 max-w-sm" data-testid="mobile-more-menu">
          <DialogHeader><DialogTitle className="text-white font-display">Menu</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-2 mt-1 max-h-[60vh] overflow-y-auto">
            <Link to="/choose-plan" onClick={() => setMoreOpen(false)} data-testid="mobile-more-plans"
              className="flex flex-col items-center justify-center gap-1.5 rounded-lg py-3 px-1 text-center border bg-[#C8000F]/15 text-[#E68A2E] border-[#C8000F]/40 active:bg-[#C8000F]/25">
              <Crown className="w-5 h-5" />
              <span className="text-[10.5px] leading-tight">Plans</span>
            </Link>
            <Link to="/contact" onClick={() => setMoreOpen(false)} data-testid="mobile-more-feedback"
              className="flex flex-col items-center justify-center gap-1.5 rounded-lg py-3 px-1 text-center border bg-white/5 text-slate-300 border-white/10 active:bg-white/10">
              <MessageSquare className="w-5 h-5" />
              <span className="text-[10.5px] leading-tight">Feedback</span>
            </Link>
            {visibleNav.filter((n) => !n.desktopOnly && !mobileNav.some((m) => m.to === n.to)).map((n) => {
              const Icon = n.icon;
              const active = loc.pathname === n.to;
              return (
                <Link key={n.to} to={n.to} onClick={() => setMoreOpen(false)} data-testid={`mobile-more-${n.to}`}
                  className={`flex flex-col items-center justify-center gap-1.5 rounded-lg py-3 px-1 text-center border ${active ? "bg-[#C8000F] text-white border-[#C8000F]" : "bg-white/5 text-slate-300 border-white/10 active:bg-white/10"}`}>
                  <Icon className="w-5 h-5" />
                  <span className="text-[10.5px] leading-tight">{n.label}</span>
                  {n.badge && <span className={`text-[9px] font-bold px-1.5 rounded ${n.badgeAmber ? "bg-[#E68A2E]" : "bg-[#C8000F]"} text-white`}>{n.badge}</span>}
                </Link>
              );
            })}
          </div>
          <div className="mt-3 pt-3 border-t border-white/10">
            <div className="text-[11px] text-slate-500 mb-2 px-1 truncate" data-testid="mobile-more-account">Signed in as <span className="text-slate-300 font-semibold">{user?.email || "—"}</span></div>
            <button onClick={() => { setMoreOpen(false); logout(); }} data-testid="mobile-more-logout"
              className="w-full flex items-center justify-center gap-2 rounded-lg py-3 text-sm font-semibold border border-[#C8000F]/40 bg-[#C8000F]/15 text-[#F26D77] active:bg-[#C8000F]/25">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {tickerInfo && <RodeoInfoDialog data={tickerInfo} onClose={() => setTickerInfo(null)} />}
    </div>
  );
}
